import { Component, OnInit, OnDestroy, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormArray, Validators, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { BookingService, ValidateBookingRequest, Passenger, StartTransactionResponse } from '../../services/booking.service';
import { SearchService } from '../../services/search.service';
import { HotelService } from '../../services/hotel.service';
import { AccountService, SavedPassenger, InvoiceProfile } from '../../services/account.service';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../../services/auth.service';
import { finalize, catchError, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { LoadingComponent } from '../../shared/components/loading/loading.component';

@Component({
  selector: 'app-passenger-info',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, RouterLink, LoadingComponent],
  templateUrl: './passenger-info.component.html',
  styleUrls: ['./passenger-info.component.css']
})
export class PassengerInfoComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly bookingService = inject(BookingService);
  private readonly searchService = inject(SearchService);
  private readonly hotelService = inject(HotelService);
  private readonly accountService = inject(AccountService);
  private readonly authService = inject(AuthService);
  private readonly cdr = inject(ChangeDetectorRef);

  savedPassengers: SavedPassenger[] = [];
  selectedSavedByIndex: { [index: number]: number | null } = {};

  // Kurumsal fatura
  invoiceProfiles: InvoiceProfile[] = [];
  wantsCorporateInvoice = false;
  selectedInvoiceProfileId: number | null = null;
  corpInvoice = {
    companyName: '', taxOffice: '', taxNumber: '',
    companyAddress: '', companyCity: '', companyPostalCode: '',
    companyPhoneNumber: '', companyEmail: ''
  };

  passengerForm!: FormGroup;
  productKey: string = '';
  roomKey: string = '';
  isLoading = false;
  errorMessage: string | null = null;
  searchParams: any = null;
  passengerCount = 1;
  passengerTypes: ('adult' | 'child')[] = [];
  passengerAges: number[] = [];
  originalPassengerKeys: string[] = [];

  // Booking summary
  bookingSummary: { hotelName: string; hotelImage: string; hotelStars: number; roomType: string; mealType: string; checkIn: string; checkOut: string; adultCount: number; childCount: number; roomCount: number } | null = null;

  // Addon state
  addons: any[] = [];
  selectedAddons: { [key: string]: boolean } = {};
  selectedAddonsByPassenger: { [addonKey: string]: { [passengerKey: string]: boolean } } = {};
  isAddonProcessing: { [key: string]: boolean } = {};
  addonError: string | null = null;
  addonBasePrice: any = null;
  addonTotalPrice: any = null;
  currentTotalPrice = 0;
  addonTraceId: string = '';
  addonPassengerKeys: string[] = [];

  // Modal kontrolleri
  showTermsModal = false;
  showPrivacyModal = false;
  termsAccepted = false;
  privacyAccepted = false;

  ngOnInit(): void {
    // Sayfa arkaplanını beyaz yap (navbar ile summary arasında gri olmasın)
    const main = document.querySelector('main.main-content') as HTMLElement | null;
    if (main) main.style.backgroundColor = '#ffffff';
    document.body.style.backgroundColor = '#ffffff';

    // 1. Önce tüm verileri yükle
    this.loadAddonData();
    this.bookingSummary = this.searchService.getBookingSummary();

    // Giriş yaptıysa kayıtlı kişileri çek
    if (this.authService.isAuthenticated()) {
      this.accountService.getPassengers().subscribe({
        next: (list) => {
          this.savedPassengers = list;
          const self = list.find(p => p.isSelf);
          if (self) {
            setTimeout(() => this.applySavedPassenger(0, self.id), 0);
          }
        },
        error: () => {}
      });

      this.accountService.getInvoiceProfiles().subscribe({
        next: (list) => {
          this.invoiceProfiles = list.filter(p => p.type === 'Corporate');
        },
        error: () => {}
      });
    }

    // URL parametrelerini al
    this.route.queryParams.subscribe(params => {
      this.productKey = params['productKey'] || '';
      this.roomKey = params['roomKey'] || '';
    });

    // 2. Passenger key'leri: SearchService'ten al (search response items[].rooms[].passengerKeys)
    this.originalPassengerKeys = [...this.searchService.getPassengerKeys()];

    // 3. Yolcu sayısı/tip/yaş belirleme
    if (this.bookingSummary && this.bookingSummary.adultCount > 0) {
      const adults = this.bookingSummary.adultCount;
      const children = this.bookingSummary.childCount || 0;
      this.passengerCount = adults + children;
      this.passengerTypes = [];
      this.passengerAges = [];
      for (let i = 0; i < adults; i++) { this.passengerTypes.push('adult'); this.passengerAges.push(35); }
      const userCriteria = this.searchService.getUserSearchCriteria();
      const childAges = userCriteria?.childrenAges || [];
      for (let i = 0; i < children; i++) { this.passengerTypes.push('child'); this.passengerAges.push(childAges[i] || 5); }
    } else {
      // addonPassengerKeys varsa ondan sayı al
      this.passengerCount = this.addonPassengerKeys.length || 1;
      this.passengerTypes = this.addonPassengerKeys.map(() => 'adult' as const);
      this.passengerAges = this.addonPassengerKeys.map(() => 35);
      if (this.passengerCount === 0) {
        this.passengerCount = 1;
        this.passengerTypes = ['adult'];
        this.passengerAges = [35];
      }
    }

    // 4. Tüm veriler hazır, şimdi formu oluştur (key'ler originalPassengerKeys'ten alınacak)
    this.initializeForm();

    // Search parametrelerini al (searchParams validate isteğinde kullanılıyor)
    this.searchService.searchResults$.subscribe(results => {
      if (results) {
        this.searchParams = results.searchCriteria;
      }
    });
  }

  private initializeForm(): void {
    this.passengerForm = this.fb.group({
      passengers: this.fb.array([]),
      isAggreementRead: [false, Validators.requiredTrue],
      note: ['']
    });

    // İlk yolcu için form ekle
    this.updatePassengerForms();
  }

  private updatePassengerForms(): void {
    const passengersArray = this.passengerForm.get('passengers') as FormArray;
    passengersArray.clear();

    for (let i = 0; i < this.passengerCount; i++) {
      passengersArray.push(this.createPassengerForm(i === 0, i));
    }
  }

  private createPassengerForm(isMaster: boolean, index?: number): FormGroup {
    // Orijinal key varsa onu kullan, yoksa yeni oluştur
    const passengerKey = (index !== undefined && this.originalPassengerKeys[index])
      ? this.originalPassengerKeys[index]
      : this.generatePassengerKey();

    // Sadece ilk yolcu (ana yolcu) için email, telefon, adres zorunlu
    const isFirstPassenger = index === 0;
    
    // Yolcu tipini belirle - search'ten gelen bilgiye göre 0=Adult, 1=Child
    const passengerTypeValue = (index !== undefined && this.passengerTypes[index]) 
      ? (this.passengerTypes[index] === 'adult' ? 0 : 1)
      : 0; // Varsayılan Adult (0)
    
    // Yaşı belirle - yetişkin için 35, çocuk için search'ten gelen yaş
    const ageValue = (index !== undefined && this.passengerAges[index] !== undefined)
      ? this.passengerAges[index]
      : (passengerTypeValue === 0 ? 35 : 5); // Varsayılan: yetişkin 35, çocuk 5
      
    return this.fb.group({
      key: [passengerKey],
      isMaster: [isMaster],
      name: ['', [Validators.required, Validators.minLength(2)]],
      surname: ['', [Validators.required, Validators.minLength(2)]],
      birthday: ['', Validators.required],
      gender: [null, Validators.required], // 0=Male, 1=Female, 2=Unknown - Kullanıcı seçimi gerekli
      tcKimlikNo: ['', [Validators.required, Validators.pattern(/^\d{11}$/)]],
      hesCode: [''],  // Boş string gönder - backend için gerekli
      age: [ageValue], // Yaş bilgisi
      // Sadece ilk yolcu için zorunlu
      email: ['', isFirstPassenger ? [Validators.required, Validators.email] : []],
      address: this.fb.group({
        street: ['', isFirstPassenger ? Validators.required : []],
        postalCode: ['', isFirstPassenger ? [Validators.required, Validators.pattern(/^\d{5}$/)] : []],
        country: this.fb.group({
          id: [277], // Türkiye ID'si
          label: ['Türkiye'],
          parentId: [13],
          parentRegion: [null]
        })
      }),
      phoneNumbers: this.fb.array([
        this.fb.group({
          countryCode: ['90'],
          areaCode: [''],
          number: ['', isFirstPassenger ? [Validators.required, Validators.pattern(/^\d{7,10}$/)] : []],
          type: ['Mobile']
        })
      ]),
      country: this.fb.group({
        id: [277],
        label: ['Türkiye']
      }),
      // Yolcu tipi - 0=Adult, 1=Child, 2=Infant
      passengerType: [passengerTypeValue],
      id: [0],
      passportTypeId: [0],
      mernisQueryStatus: ['WrongInformation'],
      milesCode: [null],
      passportNumber: [null],
      prefix: [null],
      zipCode: [null]
    });
  }

  onCorporateInvoiceToggle(): void {
    if (!this.wantsCorporateInvoice) {
      this.selectedInvoiceProfileId = null;
      return;
    }
    const def = this.invoiceProfiles.find(p => p.isDefault);
    if (def) this.applyInvoiceProfile(def.id);
  }

  applyInvoiceProfile(id: number | string | null): void {
    if (id == null || id === '') {
      this.selectedInvoiceProfileId = null;
      return;
    }
    const pid = Number(id);
    const p = this.invoiceProfiles.find(x => x.id === pid);
    if (!p) return;
    this.selectedInvoiceProfileId = pid;
    this.corpInvoice = {
      companyName: p.companyName || '',
      taxOffice: p.taxOffice || '',
      taxNumber: p.taxNumber || '',
      companyAddress: p.companyAddress || '',
      companyCity: p.companyCity || '',
      companyPostalCode: p.companyPostalCode || '',
      companyPhoneNumber: p.companyPhoneNumber || '',
      companyEmail: p.companyEmail || ''
    };
  }

  isPassengerUsedElsewhere(passengerId: number, currentIndex: number): boolean {
    return Object.entries(this.selectedSavedByIndex).some(
      ([idx, id]) => id === passengerId && Number(idx) !== currentIndex
    );
  }

  applySavedPassenger(index: number, passengerId: number | string | null): void {
    if (passengerId == null || passengerId === '') {
      this.selectedSavedByIndex[index] = null;
      return;
    }
    const id = Number(passengerId);
    const sp = this.savedPassengers.find(p => p.id === id);
    if (!sp) return;
    this.selectedSavedByIndex[index] = id;

    const grp = this.passengersArray.at(index) as FormGroup;
    if (!grp) return;
    grp.patchValue({
      name: sp.firstName,
      surname: sp.lastName,
      tcKimlikNo: sp.tcNumber || '',
      birthday: sp.birthDate ? sp.birthDate.slice(0, 10) : '',
      gender: sp.gender === 'Female' ? 1 : sp.gender === 'Male' ? 0 : null,
      email: sp.email || grp.get('email')?.value
    });
    if (sp.phoneNumber) {
      const phone = sp.phoneNumber.replace(/\D/g, '');
      const phones = grp.get('phoneNumbers') as FormArray;
      if (phones && phones.at(0)) {
        const last10 = phone.slice(-10);
        phones.at(0).patchValue({
          countryCode: '90',
          number: last10
        });
      }
    }
  }

  private generatePassengerKey(): string {
    return Math.random().toString(36).substr(2, 6);
  }

  private generateTraceId(): string {
    // UUID benzeri bir traceId oluştur
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  get passengersArray(): FormArray {
    return this.passengerForm.get('passengers') as FormArray;
  }

  getPassengerPhoneNumbers(passengerIndex: number): FormArray {
    return this.passengersArray.at(passengerIndex).get('phoneNumbers') as FormArray;
  }

  getPassengerLabel(index: number): string {
    if (this.passengerTypes.length === 0) {
      return index === 0 ? 'Ana Yolcu' : `${index + 1}. Yolcu`;
    }
    
    const type = this.passengerTypes[index];
    const typeText = type === 'adult' ? 'Yetişkin' : 'Çocuk';
    
    // Aynı tipten kaçıncı olduğunu bul
    const sameTypeCount = this.passengerTypes.slice(0, index + 1).filter(t => t === type).length;
    
    if (index === 0) {
      return `Ana Yolcu (${sameTypeCount}. ${typeText})`;
    } else {
      return `${sameTypeCount}. ${typeText}`;
    }
  }

  onSubmit(): void {
    if (this.passengerForm.valid) {
      this.validateBooking();
    } else {
      this.markFormGroupTouched(this.passengerForm);
      this.errorMessage = 'Lütfen tüm zorunlu alanları doldurunuz.';
    }
  }

  private validateBooking(): void {
    this.isLoading = true;
    this.errorMessage = null;

    // ProductKey kontrolü
    if (!this.productKey || this.productKey.trim() === '') {
      this.errorMessage = 'Oda bilgisi yüklenemedi. Lütfen sayfayı yenileyip tekrar deneyin.';
      this.isLoading = false;
      return;
    }

    // TraceId'yi cookie'den al
    const traceId = this.getCookie('TraceId');

    const formValue = this.passengerForm.value;
    const passengers: Passenger[] = formValue.passengers.map((p: any, index: number) => {
      return {
        ...p,
        birthday: new Date(p.birthday).toISOString()
      };
    });

    const request: ValidateBookingRequest = {
      productKey: this.productKey,
      isAggreementRead: formValue.isAggreementRead,
      note: formValue.note || 'Rezervasyon talebi',
      passengers: passengers,
      ...(traceId && { traceId }) // TraceId'yi sadece varsa ekle
    };

    this.bookingService.validateBooking(request, this.searchParams)
      .pipe(
        switchMap(validationResponse => {
          
          // ✅ ValidateBooking başarılı ve rezervasyon yapılabilir mi kontrol et
          if (validationResponse && validationResponse.success && validationResponse.isBookable) {
            
            const productKeys = [this.productKey];
            
            // ✅ TraceId'yi response'dan al ve cookie'ye set et
            if (validationResponse.traceId) {
              // Localhost için Secure flag'i kaldır
              const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
              const cookieOptions = isLocalhost 
                ? `TraceId=${validationResponse.traceId}; path=/; SameSite=Lax`
                : `TraceId=${validationResponse.traceId}; path=/; SameSite=None; Secure`;
              
              document.cookie = cookieOptions;
            }
            
            // ✅ ValidateBooking başarılı olduktan sonra StartTransaction çağır
            return this.bookingService.startTransaction(productKeys, this.searchParams);
          } else {
            // ❌ ValidateBooking başarısız - StartTransaction çağırma
            const errorMessage = validationResponse?.reasons?.[0]?.description || 'Rezervasyon doğrulanırken bir hata oluştu.';
            throw new Error(errorMessage);
          }
        }),
        finalize(() => {
          this.isLoading = false;
        }),
        catchError(error => {
          this.errorMessage = this.bookingService.parseValidationError(error);
          return of(null);
        })
      )
      .subscribe((transactionResponse: StartTransactionResponse | null) => {
        
        // ✅ Backend yanıt yapısına uygun kontrol (küçük harf)
        if (transactionResponse && transactionResponse.success && transactionResponse.data) {
          
          // Transaction başarılı - ödeme sayfasına yönlendir
          
          // ✅ Yolcu verilerini hazırla (formdan al)
          const formValue = this.passengerForm.value;
          const passengerData = {
            passengers: formValue.passengers.map((p: any) => ({
              ...p,
              birthday: p.birthday ? new Date(p.birthday).toISOString() : new Date().toISOString()
            })),
            isAggreementRead: formValue.isAggreementRead,
            note: formValue.note || 'Rezervasyon talebi'
          };
          
          
          // ✅ SessionStorage'a kaydet (Navigation state kaybolsa bile kalır)
          sessionStorage.setItem('currentPassengerData', JSON.stringify(passengerData));
          sessionStorage.setItem('currentTransactionKey', transactionResponse.data.transactionKey);

          // Kurumsal fatura bilgisi (varsa) ödeme sayfasında kullanmak için kaydet
          if (this.wantsCorporateInvoice) {
            const corpPayload = {
              type: 'Corporate',
              ...this.corpInvoice
            };
            sessionStorage.setItem('currentInvoiceInfo', JSON.stringify(corpPayload));
          } else {
            sessionStorage.removeItem('currentInvoiceInfo');
          }
          
          // ✅ State ile navigation - gerçek passenger data'sı gönderiliyor
          this.router.navigate(['/odeme'], {
            queryParams: {
              productKey: this.productKey,
              roomKey: this.roomKey,
              transactionKey: transactionResponse.data.transactionKey
            },
            state: {
              passengerData: passengerData,
              searchParams: this.searchParams
            }
          }).then(result => {
            if (result) {
            } else {
            }
          }).catch(error => {
          });
        } else if (transactionResponse && !transactionResponse.success) {
          this.errorMessage = 'Rezervasyon işlemi başlatılamadı. Lütfen tekrar deneyin.';
        } else {
          this.errorMessage = 'Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.';
        }
      });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else if (control instanceof FormArray) {
        control.controls.forEach(arrayControl => {
          if (arrayControl instanceof FormGroup) {
            this.markFormGroupTouched(arrayControl);
          } else {
            arrayControl.markAsTouched();
          }
        });
      } else {
        control?.markAsTouched();
      }
    });
  }

  // Debug fonksiyonu - form durumunu kontrol etmek için
  checkFormStatus(): void {
    // Form validation status check - removed console logs
  }

  private loadAddonData(): void {
    const data = this.searchService.getAddonData();
    if (data) {
      this.addonBasePrice = data.basePrice;
      this.addonTotalPrice = data.totalPrice;
      this.addonTraceId = data.traceId;
      this.addonPassengerKeys = data.passengerKeys;

      if (this.addonTotalPrice?.value) {
        this.currentTotalPrice = this.addonTotalPrice.value;
      } else if (this.addonBasePrice?.value) {
        this.currentTotalPrice = this.addonBasePrice.value;
      }

      if (data.addons?.length > 0) {
        this.addons = data.addons;
        this.addons.forEach(addon => {
          const isSelected = addon.isRequired || false;
          this.selectedAddons[addon.key] = isSelected;
          // Yolcu bazlı state
          this.selectedAddonsByPassenger[addon.key] = {};
          (this.addonPassengerKeys || []).forEach(pk => {
            this.selectedAddonsByPassenger[addon.key][pk] = isSelected;
          });
        });
      }
    }
  }

  getAddonPrice(addon: any): number {
    let basePrice = 0;
    if (addon.price?.value !== undefined) {
      basePrice = addon.price.value;
    } else if (addon.amount?.value !== undefined) {
      basePrice = addon.amount.value;
    }
    const count = this.addonPassengerKeys?.length || 1;
    return basePrice * count;
  }

  toggleAddon(addonKey: string, event: any): void {
    const isSelected = event.target.checked;

    if (!this.addonPassengerKeys?.length) {
      this.addonError = 'Bir sorun oluştu. Lütfen sayfayı yenileyip tekrar deneyin.';
      event.target.checked = !isSelected;
      return;
    }

    if (!this.productKey) {
      this.addonError = 'Oda bilgisi yüklenemedi. Lütfen sayfayı yenileyin.';
      event.target.checked = !isSelected;
      return;
    }

    const traceId = this.addonTraceId || this.getCookie('TraceId');
    if (!traceId) {
      this.addonError = 'Oturumunuzun süresi dolmuş. Lütfen sayfayı yenileyip tekrar deneyin.';
      event.target.checked = !isSelected;
      return;
    }

    const operations = this.addonPassengerKeys.map(pk => ({
      addOnKey: addonKey,
      passengerKey: pk,
      type: isSelected ? 'Add' as const : 'Remove' as const
    }));

    this.isAddonProcessing[addonKey] = true;
    this.addonError = null;


    this.hotelService.manageAddOn({
      productKey: this.productKey,
      traceId,
      operations
    }).pipe(
      finalize(() => {
        this.isAddonProcessing[addonKey] = false;
        this.cdr.detectChanges();
      }),
      catchError(error => {
        this.addonError = isSelected
          ? 'Ek hizmet eklenirken bir sorun oluştu. Lütfen tekrar deneyin.'
          : 'Ek hizmet kaldırılırken bir sorun oluştu. Lütfen tekrar deneyin.';
        event.target.checked = !isSelected;
        return of(null);
      })
    ).subscribe(response => {
      if (!response) return;


      if (response.success !== false) {
        this.selectedAddons[addonKey] = isSelected;

        if (response.productKey) {
          this.productKey = response.productKey;
        }

        if (response.traceId) {
          this.addonTraceId = response.traceId;
        }

        let updatedPrice: number | null = null;
        if (response.totalPrice != null) {
          updatedPrice = typeof response.totalPrice === 'number' ? response.totalPrice : response.totalPrice.value;
        } else if (response.basketLeastwiseAmount != null) {
          updatedPrice = typeof response.basketLeastwiseAmount === 'number' ? response.basketLeastwiseAmount : response.basketLeastwiseAmount.value;
        } else if (response.balance != null) {
          updatedPrice = typeof response.balance === 'number' ? response.balance : response.balance.value;
        }

        if (updatedPrice != null) {
          this.currentTotalPrice = updatedPrice;
        }

        if (response.additionalAddons) {
          const addonsObj: any = response.additionalAddons;
          const keys = Object.keys(addonsObj);
          if (keys.length > 0) {
            this.addonPassengerKeys = keys;
          }

          if (!isSelected) {
            keys.forEach(pk => {
              const passengerAddons = addonsObj[pk];
              if (Array.isArray(passengerAddons)) {
                const stillExists = passengerAddons.find((a: any) => a.key === addonKey);
                if (!stillExists) {
                  this.selectedAddons[addonKey] = false;
                }
              }
            });
          }
        } else if (!isSelected) {
          this.selectedAddons[addonKey] = false;
        }

        this.cdr.detectChanges();
      } else {
        event.target.checked = !isSelected;
        this.addonError = 'İşlem gerçekleştirilemedi. Lütfen tekrar deneyin.';
      }
    });
  }

  toggleAddonForPassenger(addonKey: string, passengerKey: string, event: any): void {
    const isSelected = event.target.checked;

    if (!this.productKey) {
      this.addonError = 'Oda bilgisi yüklenemedi. Lütfen sayfayı yenileyin.';
      event.target.checked = !isSelected;
      return;
    }

    const traceId = this.addonTraceId || this.getCookie('TraceId');
    if (!traceId) {
      this.addonError = 'Oturumunuzun süresi dolmuş.';
      event.target.checked = !isSelected;
      return;
    }

    const processingKey = `${addonKey}_${passengerKey}`;
    this.isAddonProcessing[processingKey] = true;
    this.addonError = null;

    this.hotelService.manageAddOn({
      productKey: this.productKey,
      traceId,
      operations: [{
        addOnKey: addonKey,
        passengerKey: passengerKey,
        type: isSelected ? 'Add' as const : 'Remove' as const
      }]
    }).pipe(
      finalize(() => {
        this.isAddonProcessing[processingKey] = false;
        this.cdr.detectChanges();
      }),
      catchError(error => {
        this.addonError = 'Ek hizmet güncellenirken bir sorun oluştu.';
        event.target.checked = !isSelected;
        return of(null);
      })
    ).subscribe(response => {
      if (!response) return;

      if (response.success !== false) {
        this.selectedAddonsByPassenger[addonKey][passengerKey] = isSelected;

        // Tüm yolcular seçili mi kontrol et
        const allSelected = this.addonPassengerKeys.every(pk => this.selectedAddonsByPassenger[addonKey]?.[pk]);
        const anySelected = this.addonPassengerKeys.some(pk => this.selectedAddonsByPassenger[addonKey]?.[pk]);
        this.selectedAddons[addonKey] = anySelected;

        if (response.productKey) this.productKey = response.productKey;
        if (response.traceId) this.addonTraceId = response.traceId;

        let updatedPrice: number | null = null;
        if (response.totalPrice != null) {
          updatedPrice = typeof response.totalPrice === 'number' ? response.totalPrice : response.totalPrice.value;
        } else if (response.basketLeastwiseAmount != null) {
          updatedPrice = typeof response.basketLeastwiseAmount === 'number' ? response.basketLeastwiseAmount : response.basketLeastwiseAmount.value;
        } else if (response.balance != null) {
          updatedPrice = typeof response.balance === 'number' ? response.balance : response.balance.value;
        }

        if (updatedPrice != null) {
          this.currentTotalPrice = updatedPrice;
        }

        this.cdr.detectChanges();
      } else {
        event.target.checked = !isSelected;
        this.addonError = 'İşlem gerçekleştirilemedi.';
      }
    });
  }

  getPassengerLabel2(passengerKey: string): string {
    const index = this.addonPassengerKeys.indexOf(passengerKey);
    if (index === -1) return passengerKey;
    if (this.passengerTypes.length > 0) {
      const type = this.passengerTypes[index];
      const typeText = type === 'adult' ? 'Yetişkin' : 'Çocuk';
      const sameTypeCount = this.passengerTypes.slice(0, index + 1).filter(t => t === type).length;
      return `${sameTypeCount}. ${typeText}`;
    }
    return `${index + 1}. Yolcu`;
  }

  getDiscountPercent(): number {
    if (!this.addonBasePrice?.value || this.addonBasePrice.value <= this.currentTotalPrice) return 0;
    return Math.round(((this.addonBasePrice.value - this.currentTotalPrice) / this.addonBasePrice.value) * 100);
  }

  getPricePerNight(): number {
    const nights = this.getNightCount();
    if (nights <= 0) return this.currentTotalPrice;
    return Math.round(this.currentTotalPrice / nights);
  }

  getNightCount(): number {
    if (!this.bookingSummary?.checkIn || !this.bookingSummary?.checkOut) return 0;
    const checkIn = new Date(this.bookingSummary.checkIn);
    const checkOut = new Date(this.bookingSummary.checkOut);
    return Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24));
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  formatShortDate(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const day = date.getDate();
    const months = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
    const month = months[date.getMonth()];
    const year = date.getFullYear().toString().slice(-2);
    return `${day} ${month}'${year}`;
  }

  goBack(): void {
    window.history.back();
  }

  // Modal metodları
  openTermsModal(): void {
    this.showTermsModal = true;
    document.body.style.overflow = 'hidden';
  }

  openPrivacyModal(): void {
    this.showPrivacyModal = true;
    document.body.style.overflow = 'hidden';
  }

  closeModals(): void {
    this.showTermsModal = false;
    this.showPrivacyModal = false;
    document.body.style.overflow = 'auto';
  }

  acceptTerms(): void {
    this.termsAccepted = true;
    this.checkBothAccepted();
    this.closeModals();
  }

  acceptPrivacy(): void {
    this.privacyAccepted = true;
    this.checkBothAccepted();
    this.closeModals();
  }

  private checkBothAccepted(): void {
    if (this.termsAccepted && this.privacyAccepted) {
      this.passengerForm.get('isAggreementRead')?.setValue(true);
      if (this.errorMessage?.includes('Kullanım Şartları')) {
        this.errorMessage = null;
      }
    }
  }

  onAgreementClick(event: Event): void {
    if (!this.termsAccepted || !this.privacyAccepted) {
      event.preventDefault();
      this.errorMessage = ' Kullanım Şartları ve Gizlilik Politikasını okumanız gerekmektedir.';
    }
  }

  ngOnDestroy(): void {
    const main = document.querySelector('main.main-content') as HTMLElement | null;
    if (main) main.style.backgroundColor = '';
    document.body.style.backgroundColor = '';
  }

  private getCookie(name: string): string | null {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null;
    }
    return null;
  }

}
